import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import { descendantsNearView } from "./scroll-box-near-view"
import { crossRectSpan, spansOverlap } from "./scroll-box-spans"
import {
  COLLAPSED_EXTENT_PX,
  clipsAlong,
  escapesClipOnTheWayTo,
  formsContainingBlock,
  isRendered,
  isStickyPositioned,
  layoutChildren,
  layoutParent,
  nearestScrollContainer,
  overflowAlongPx,
  pageClipsAlong,
  scrollsAlong,
} from "./scroll-box-styles"

export interface EffectiveSize {
  effectivePx: number
  overflowPx: number
  stickyStartPx: number
  stickyEndPx: number
}

interface StickyExtents {
  startPx: number
  endPx: number
}

interface VisibleSlice {
  startPx: number
  endPx: number
}

function layoutAncestorsUpTo(element: Element, box: Element): Element[] {
  const ancestors: Element[] = []
  for (let parent = layoutParent(element); parent && box.contains(parent); parent = layoutParent(parent)) {
    ancestors.push(parent)
  }
  return ancestors
}

function stickyContainingBlock(sticky: Element, box: Element): Element {
  return layoutAncestorsUpTo(sticky, box).find((ancestor) => formsContainingBlock(ancestor)) ?? box
}

function hasContentScrollingBeneath(sticky: Element, box: Element, axis: ScrollAxis): boolean {
  const stickySpanAcross = crossRectSpan(sticky.getBoundingClientRect(), axis)
  return layoutAncestorsUpTo(sticky, stickyContainingBlock(sticky, box)).some((ancestor) =>
    layoutChildren(ancestor).some(
      (child) =>
        !child.contains(sticky) &&
        isRendered(child) &&
        spansOverlap(crossRectSpan(child.getBoundingClientRect(), axis), stickySpanAcross),
    ),
  )
}

function measureStickyExtents(box: Element, axis: ScrollAxis): StickyExtents {
  const extents: StickyExtents = { startPx: 0, endPx: 0 }
  for (const descendant of descendantsNearView(box)) {
    if (!isStickyPositioned(descendant) || !isRendered(descendant)) continue
    if (nearestScrollContainer(descendant) !== box) continue
    if (!hasContentScrollingBeneath(descendant, box, axis)) continue
    const style = getComputedStyle(descendant)
    const extentPx = descendant.getBoundingClientRect()[axis.rectExtent]
    if (style[axis.rectStart] !== "auto") extents.startPx = Math.max(extents.startPx, extentPx)
    if (style[axis.rectEnd] !== "auto") extents.endPx = Math.max(extents.endPx, extentPx)
  }
  return extents
}

function viewportExtentPx(axis: ScrollAxis): number {
  return window.visualViewport ? window.visualViewport[axis.viewportExtent] : window[axis.windowExtent]
}

function ancestorsBelowBody(box: Element): Element[] {
  const ancestors: Element[] = []
  for (let ancestor = box.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
    ancestors.push(ancestor)
  }
  return ancestors
}

function scrollingAncestorCapsPx(box: Element, axis: ScrollAxis): number[] {
  const caps = ancestorsBelowBody(box)
    .filter((ancestor) => scrollsAlong(ancestor, axis))
    .map((ancestor) => ancestor[axis.clientSize])
  return pageClipsAlong(axis) ? caps : [...caps, viewportExtentPx(axis)]
}

function enclosingScroller(box: Element, axis: ScrollAxis): Element | null {
  return (
    ancestorsBelowBody(box).find((ancestor) => scrollsAlong(ancestor, axis) && !escapesClipOnTheWayTo(box, ancestor)) ??
    null
  )
}

function measureVisibleSlice(box: Element, axis: ScrollAxis): VisibleSlice {
  const boxRect = box.getBoundingClientRect()
  const slice: VisibleSlice = { startPx: boxRect[axis.rectStart], endPx: boxRect[axis.rectEnd] }
  const scroller = enclosingScroller(box, axis)
  for (const ancestor of ancestorsBelowBody(box)) {
    if (ancestor === scroller) return slice
    if (scrollsAlong(ancestor, axis) || !clipsAlong(ancestor, axis) || escapesClipOnTheWayTo(box, ancestor)) continue
    const ancestorRect = ancestor.getBoundingClientRect()
    slice.startPx = Math.max(slice.startPx, ancestorRect[axis.rectStart])
    slice.endPx = Math.min(slice.endPx, ancestorRect[axis.rectEnd])
  }
  if (pageClipsAlong(axis)) {
    slice.startPx = Math.max(slice.startPx, 0)
    slice.endPx = Math.min(slice.endPx, viewportExtentPx(axis))
  }
  return slice
}

export function isClippedOutOfSight(box: Element): boolean {
  return SCROLL_AXES.some((axis) => {
    const slice = measureVisibleSlice(box, axis)
    if (slice.endPx - slice.startPx <= COLLAPSED_EXTENT_PX) return true
    const scroller = enclosingScroller(box, axis)
    return scroller !== null && isClippedOutOfSight(scroller)
  })
}

export function measureEffectiveSize(box: Element, axis: ScrollAxis): EffectiveSize {
  const style = getComputedStyle(box)
  const sticky = measureStickyExtents(box, axis)
  const paddedPx = box[axis.clientSize] - parseFloat(style[axis.paddingStart]) - parseFloat(style[axis.paddingEnd])
  const slice = measureVisibleSlice(box, axis)
  return {
    effectivePx: Math.min(
      paddedPx - sticky.startPx - sticky.endPx,
      ...scrollingAncestorCapsPx(box, axis),
      Math.max(0, slice.endPx - slice.startPx),
    ),
    overflowPx: overflowAlongPx(box, axis),
    stickyStartPx: sticky.startPx,
    stickyEndPx: sticky.endPx,
  }
}
